import { useMemo } from 'react';
import type { FeedNote, InteractionWsPayload } from '@/hooks/types';
import type { GameLifecycleState } from '@/store/gameSlice';
import { PersonaAvatar } from '@/components/PersonaAvatar';
import { PersonaMenu } from '@/scenes/components/PersonaMenu';
import { ActorLine } from '@/scenes/components/ActorLine';
import { FeedNoteBlock } from './FeedNoteBlock';
import { FeedBlockFrame } from './FeedBlockFrame';
import { feedItemKey } from '../feedChips';
import { interleaveNotes } from '../feedRows';
import { useStickToBottom } from '../hooks/useStickToBottom';
import type { RoomData } from './RoomPanel';

interface ExplorationReaderProps {
  room: RoomData | null;
  ambientInteractions?: InteractionWsPayload[];
  lifecycleState?: GameLifecycleState;
  /** Typed text lines for this character (#3856), shown among the ambient poses by time. */
  notes?: FeedNote[];
  onRetry?: () => void;
  /** Server refusal or untyped entry output, shown before a room exists. */
  entryError?: string;
}

/**
 * The quiet-room reader. Room facts stay structured and ambient interaction
 * frames are rendered as individual readable entries, never as a transcript.
 */
export function ExplorationReader({
  room,
  ambientInteractions = [],
  lifecycleState,
  notes = [],
  onRetry,
  entryError,
}: ExplorationReaderProps) {
  const awaitingSnapshot = lifecycleState === 'entering' && Boolean(room);
  const isStale =
    lifecycleState === 'reconnecting' || lifecycleState === 'entry-error' || awaitingSnapshot;
  const isAftermath = lifecycleState === 'aftermath';
  const visibleEntryError =
    lifecycleState === 'entering' || lifecycleState === 'entry-error'
      ? (entryError ?? notes.at(-1)?.content)
      : undefined;
  // One column (#3856): the room's structured poses and the character's own
  // notes (a look, an error, an arrival) in the order they happened, never a
  // section of notes below a section of poses.
  const rows = useMemo(
    () => interleaveNotes(ambientInteractions, notes),
    [ambientInteractions, notes]
  );
  // The sorting menu's "all from <name>" and "Minimize all" (#4128).
  const keysOf = (personaId: number) =>
    ambientInteractions
      .filter((item) => item.persona.id === personaId)
      .map((item) => feedItemKey('interaction', item.id));
  const allKeys = () => [
    ...ambientInteractions.map((item) => feedItemKey('interaction', item.id)),
    ...notes.map((note) => feedItemKey('note', note.id)),
  ];
  // The newest line stays in view as poses and notes arrive, until the reader
  // scrolls up to read; this reader owns its scroll container.
  const stick = useStickToBottom();
  return (
    <section
      ref={stick.containerRef}
      className="min-h-0 flex-1 overflow-y-auto overscroll-contain [scrollbar-gutter:stable]"
      style={{
        fontFamily: 'var(--play-prose-family, ui-sans-serif)',
        fontSize: 'var(--play-prose-size, 14px)',
      }}
      aria-label="Exploration"
      data-testid="exploration-reader"
    >
      <div
        ref={stick.contentRef}
        className="mx-auto w-full max-w-[var(--play-reading-measure,none)] space-y-[var(--play-density-gap,1.25rem)] px-4 py-6 sm:px-6"
      >
        <header className="border-b pb-4">
          <p className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">
            {isStale ? 'Last confirmed location' : 'You are here'}
          </p>
          <h1 className="mt-1 font-serif text-2xl">{room?.name ?? 'The world'}</h1>
          {isStale && (
            <p className="mt-2 text-sm text-muted-foreground" role="status">
              {awaitingSnapshot
                ? 'Refreshing your confirmed surroundings…'
                : 'This view may be out of date while the connection recovers.'}
            </p>
          )}
        </header>
        {isAftermath && (
          <div className="rounded-lg border bg-muted/30 p-4" role="status">
            <p className="font-medium">The scene has ended.</p>
            <p className="mt-1 text-sm text-muted-foreground">
              You are back in exploration. The Here panel remains available for your next choice.
            </p>
          </div>
        )}
        {room && lifecycleState === 'entry-error' && (
          <div
            className="rounded-lg border border-destructive/40 bg-destructive/5 p-4"
            role="alert"
          >
            <p className="font-medium">The connection needs attention.</p>
            <p className="mt-1 text-sm text-muted-foreground">
              This is the last confirmed location. Try again to refresh your surroundings.
            </p>
            {onRetry && (
              <button
                type="button"
                onClick={onRetry}
                className="mt-3 min-h-11 rounded border px-4 text-sm font-medium hover:bg-accent"
              >
                Try again
              </button>
            )}
          </div>
        )}

        {!room ? (
          <div
            className="rounded-lg border border-dashed p-8 text-center"
            role={lifecycleState === 'entry-error' ? 'alert' : 'status'}
          >
            <h2 className="font-serif text-xl">
              {lifecycleState === 'entry-error'
                ? 'Entry could not be completed'
                : 'Finding your place'}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              <span className="sr-only">No messages yet. </span>
              {visibleEntryError ??
                (lifecycleState === 'entry-error'
                  ? 'The world did not confirm this character. Check your connection and try again.'
                  : 'Confirming your entry…')}
            </p>
            {lifecycleState === 'entry-error' && onRetry && (
              <button
                type="button"
                onClick={onRetry}
                className="mt-4 min-h-11 rounded border px-4 text-sm font-medium hover:bg-accent"
              >
                Try again
              </button>
            )}
          </div>
        ) : (
          <>
            {room.description ? (
              <section aria-labelledby="exploration-description">
                <h2 id="exploration-description" className="sr-only">
                  Surroundings
                </h2>
                <p className="whitespace-pre-wrap text-[length:var(--play-prose-size,14px)] leading-relaxed">
                  {room.description}
                </p>
              </section>
            ) : (
              <section
                className="rounded-lg border border-dashed p-6"
                aria-labelledby="quiet-heading"
              >
                <h2 id="quiet-heading" className="font-serif text-xl">
                  The room is quiet.
                </h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  <span className="sr-only">No messages yet. </span>
                  Look around the Here panel, inspect someone or something, or choose an exit.
                </p>
              </section>
            )}

            {rows.length > 0 && (
              <ol aria-label="Activity" className="space-y-3">
                {rows.map((row) =>
                  row.type === 'note' ? (
                    <li key={row.note.id} data-feed-row={`note:${row.note.id}`}>
                      <FeedNoteBlock note={row.note} allKeys={allKeys} />
                    </li>
                  ) : (
                    <li
                      key={`${row.item.id}:${row.item.timestamp}`}
                      data-feed-row={`interaction:${row.item.id}`}
                    >
                      <FeedBlockFrame
                        itemKey={feedItemKey('interaction', row.item.id)}
                        stub={`${row.item.persona.name} · ${new Date(row.item.timestamp).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`}
                        persona={{ id: row.item.persona.id, name: row.item.persona.name }}
                        keysOf={keysOf}
                        allKeys={allKeys}
                      >
                        <article
                          className="group relative -mx-2 rounded-md py-1 pl-2 pr-20 [display:flow-root] hover:bg-muted/40"
                          data-testid="ambient-line"
                        >
                          {/* The avatar is an indent (#4128): left-click is the persona
                              menu, on the avatar and on the name that opens the line
                              (#4791); the right button belongs to the frame's sorting
                              menu. */}
                          <span
                            className="float-left mr-2 flex"
                            data-testid="pose-avatar"
                            data-pose-avatar
                          >
                            <PersonaMenu
                              personaId={row.item.persona.id}
                              personaName={row.item.persona.name}
                              thumbnailUrl={row.item.persona.thumbnail_url}
                              leftClick
                              contextMenu={false}
                            >
                              <PersonaAvatar
                                source={{
                                  name: row.item.persona.name,
                                  thumbnailUrl: row.item.persona.thumbnail_url,
                                }}
                                size="xs"
                              />
                            </PersonaMenu>
                          </span>
                          <time
                            dateTime={row.item.timestamp}
                            title={new Date(row.item.timestamp).toLocaleString()}
                            className="absolute right-2 top-1 text-xs text-muted-foreground opacity-0 group-focus-within:opacity-100 group-hover:opacity-100"
                            data-testid="pose-time"
                          >
                            {new Date(row.item.timestamp).toLocaleTimeString([], {
                              hour: 'numeric',
                              minute: '2-digit',
                            })}
                          </time>
                          <div className="whitespace-pre-line">
                            <ActorLine
                              line={row.item.line}
                              content={row.item.content}
                              actorName={
                                row.item.attributed_companion_name ?? row.item.persona.name
                              }
                              renderName={(name) => (
                                <span data-testid="pose-actor" data-pose-actor>
                                  <PersonaMenu
                                    personaId={row.item.persona.id}
                                    personaName={row.item.persona.name}
                                    thumbnailUrl={row.item.persona.thumbnail_url}
                                    leftClick
                                    contextMenu={false}
                                  >
                                    {name}
                                  </PersonaMenu>
                                </span>
                              )}
                            />
                          </div>
                        </article>
                      </FeedBlockFrame>
                    </li>
                  )
                )}
              </ol>
            )}
          </>
        )}
      </div>
    </section>
  );
}
